import React, { useMemo } from 'react';
import { Wind, Compass, Gauge, TrendingDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
  AreaChart,
  Area,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useAnalytics } from '../../hooks/useAnalytics';
import { InfoTooltipButton } from './InfoTooltipButton';
import './WindTab.css';

// Statistics calculation helper
function calcStats(data, field) {
  const values = data.map(d => d[field]).filter(v => v != null && !isNaN(v));
  if (values.length === 0) return { mean: 0, min: 0, max: 0, stdDev: 0, n: 0 };
  
  const n = values.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);
  
  return { mean, min, max, stdDev, n };
}

// Wind speed tooltip
function WindSpeedTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  return (
    <div className="scientific-tooltip">
      <p className="scientific-tooltip__time">{format(parseISO(label), 'MMM dd, yyyy HH:mm')}</p>
      <div className="scientific-tooltip__row">
        <span style={{ color: '#4A7A8A' }}>{t('analytics.labels.field')}:</span>
        <span>{payload.find(p => p.dataKey === 'campo_speed')?.value?.toFixed(1)} m/s</span>
      </div>
      <div className="scientific-tooltip__row">
        <span style={{ color: '#5A7A3A' }}>{t('analytics.labels.underPanel')}:</span>
        <span>{payload.find(p => p.dataKey === 'retro_speed')?.value?.toFixed(1)} m/s</span>
      </div>
      {payload.find(p => p.dataKey === 'campo_gust') && (
        <>
          <div className="scientific-tooltip__row" style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
            <span style={{ color: '#4A7A8A' }}>{t('analytics.wind.series.fieldGust')}:</span>
            <span>{payload.find(p => p.dataKey === 'campo_gust')?.value?.toFixed(1)} m/s</span>
          </div>
          <div className="scientific-tooltip__row">
            <span style={{ color: '#5A7A3A' }}>{t('analytics.wind.series.underGust')}:</span>
            <span>{payload.find(p => p.dataKey === 'retro_gust')?.value?.toFixed(1)} m/s</span>
          </div>
        </>
      )}
    </div>
  );
}

// Wind direction tooltip
function WindDirTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  const data = payload[0]?.payload;
  if (!data) return null;
  
  const dirLabels = [
    t('analytics.wind.cardinal.n'),
    t('analytics.wind.cardinal.ne'),
    t('analytics.wind.cardinal.e'),
    t('analytics.wind.cardinal.se'),
    t('analytics.wind.cardinal.s'),
    t('analytics.wind.cardinal.sw'),
    t('analytics.wind.cardinal.w'),
    t('analytics.wind.cardinal.nw'),
  ];
  const dirIndex = Math.round((data.direction % 360) / 45) % 8;
  
  return (
    <div className="scientific-tooltip">
      <p className="scientific-tooltip__time">{format(parseISO(data.time), 'MMM dd HH:mm')}</p>
      <div className="scientific-tooltip__row">
        <span>{t('analytics.wind.direction')}:</span>
        <span>{data.direction}° ({dirLabels[dirIndex]})</span>
      </div>
      <div className="scientific-tooltip__row">
        <span>{t('analytics.wind.speed')}:</span>
        <span>{data.speed?.toFixed(1)} m/s</span>
      </div>
    </div>
  );
}

export function WindTab() {
  const { hourlyData, dailyData, timeRange } = useAnalytics();
  const { t } = useTranslation();

  // Wind statistics
  const windStats = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return null;

    // Calculate calm hours (wind < 0.5 m/s)
    const calmHoursCampo = campo.filter(d => (d.wind_ms || 0) < 0.5).length;
    const calmHoursRetro = retro.filter(d => (d.wind_ms || 0) < 0.5).length;

    return {
      speed: {
        campo: calcStats(campo, 'wind_ms'),
        retro: calcStats(retro, 'wind_ms'),
      },
      gust: {
        campo: calcStats(campo, 'wind_gust_ms'),
        retro: calcStats(retro, 'wind_gust_ms'),
      },
      calmHours: {
        campo: campo.length > 0 ? ((calmHoursCampo / campo.length) * 100).toFixed(1) : '0',
        retro: retro.length > 0 ? ((calmHoursRetro / retro.length) * 100).toFixed(1) : '0',
      },
    };
  }, [hourlyData]);

  // Wind speed chart data
  const windSpeedData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];

    const step = Math.max(1, Math.floor(campo.length / 200));
    
    return campo.filter((_, i) => i % step === 0).map((c, idx) => {
      const r = retro[idx * step] || {};
      return {
        time: c.ts_iso,
        campo_speed: c.wind_ms || 0,
        retro_speed: r.wind_ms || 0,
        campo_gust: c.wind_gust_ms || 0,
        retro_gust: r.wind_gust_ms || 0,
      };
    });
  }, [hourlyData]);

  // Wind direction data for scatter plot
  const windDirectionData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    
    if (campo.length === 0) return [];

    // Sample every 8th point for readability
    const step = Math.max(1, Math.floor(campo.length / 100));
    
    return campo.filter((_, i) => i % step === 0).map(c => ({
      time: c.ts_iso,
      direction: c.wind_dir_deg || 0,
      speed: c.wind_ms || 0,
    }));
  }, [hourlyData]);

  // Wind rose data with speed bins (analytical)
  const windRoseData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    if (campo.length === 0) return { directions: [], speedBins: [], calm: 0, total: 0 };
    
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    // Speed bins: 0-1.54, 1.54-3.09, 3.09-5.14, 5.14-8.23, 8.23-10.80, 10.80-15.50, >15.50
    const speedRanges = [
      { min: 0, max: 1.54, color: '#4A90E2', label: '0.00 - 1.54' },
      { min: 1.54, max: 3.09, color: '#2E7D8F', label: '1.54 - 3.09' },
      { min: 3.09, max: 5.14, color: '#5CB85C', label: '3.09 - 5.14' },
      { min: 5.14, max: 8.23, color: '#7CB342', label: '5.14 - 8.23' },
      { min: 8.23, max: 10.80, color: '#9CCC65', label: '8.23 - 10.80' },
      { min: 10.80, max: 15.50, color: '#FDD835', label: '10.80 - 15.50' },
      { min: 15.50, max: Infinity, color: '#FF6F00', label: '> 15.50' },
    ];
    
    // Initialize bins: 8 directions x 7 speed ranges
    const bins = Array(8).fill(null).map(() => Array(7).fill(0));
    let calmCount = 0;
    let totalCount = 0;
    
    campo.forEach(d => {
      const speed = d.wind_ms || 0;
      const dir = d.wind_dir_deg || 0;
      
      totalCount++;
      
      // Check for calm (< 0.5 m/s)
      if (speed < 0.5) {
        calmCount++;
        return;
      }
      
      // Find direction bin
      const dirIndex = Math.round(dir / 45) % 8;
      
      // Find speed bin
      const speedIndex = speedRanges.findIndex(range => speed >= range.min && speed < range.max);
      if (speedIndex >= 0) {
        bins[dirIndex][speedIndex]++;
      }
    });
    
    // Calculate frequencies for each direction-speed combination
    const directionData = directions.map((dir, dirIdx) => {
      const dirTotal = bins[dirIdx].reduce((a, b) => a + b, 0);
      const speedSegments = speedRanges.map((range, speedIdx) => {
        const count = bins[dirIdx][speedIdx];
        const freq = totalCount > 0 ? (count / totalCount) * 100 : 0;
        return {
          ...range,
          count,
          frequency: freq,
          cumulativeFreq: speedRanges.slice(0, speedIdx + 1).reduce((sum, _, idx) => 
            sum + (totalCount > 0 ? (bins[dirIdx][idx] / totalCount) * 100 : 0), 0
          ),
        };
      });
      
      return {
        direction: dir,
        totalCount: dirTotal,
        totalFrequency: totalCount > 0 ? (dirTotal / totalCount) * 100 : 0,
        speedSegments,
      };
    });
    
    // Calculate speed bin totals (across all directions)
    const speedBinTotals = speedRanges.map((range, speedIdx) => {
      const total = bins.reduce((sum, dirBins) => sum + dirBins[speedIdx], 0);
      const freq = totalCount > 0 ? (total / totalCount) * 100 : 0;
      return {
        ...range,
        count: total,
        frequency: freq,
      };
    });
    
    const calmFreq = totalCount > 0 ? (calmCount / totalCount) * 100 : 0;
    
    return {
      directions: directionData,
      speedBins: speedBinTotals,
      calm: {
        count: calmCount,
        frequency: calmFreq,
      },
      total: totalCount,
    };
  }, [hourlyData]);

  // Get prevailing direction
  const prevailingDirection = useMemo(() => {
    if (!windRoseData || !windRoseData.directions || windRoseData.directions.length === 0) return t('common.notAvailable');
    const max = windRoseData.directions.reduce((prev, curr) => 
      curr.totalFrequency > prev.totalFrequency ? curr : prev
    );
    return `${max.direction} (${max.totalFrequency.toFixed(1)}%)`;
  }, [windRoseData, t]);

  // Wind reduction percentage
  const windReduction = useMemo(() => {
    if (!windStats || windStats.speed.campo.mean === 0) return 0;
    return ((1 - windStats.speed.retro.mean / windStats.speed.campo.mean) * 100).toFixed(1);
  }, [windStats]);

  // X-axis formatter
  const getXAxisFormat = () => {
    const days = timeRange?.days || 14;
    if (days <= 1) return 'HH:mm';
    if (days <= 7) return 'EEE HH:mm';
    return 'MMM dd';
  };

  const xAxisFormat = getXAxisFormat();

  return (
    <div className="wind-tab">
      {/* Wind Speed Time Series */}
      <section className="wind-tab__section">
        <div className="wind-tab__section-header">
          <div className="wind-tab__section-title-group">
            <Gauge size={20} className="wind-tab__section-icon" />
            <h3 className="wind-tab__section-title">{t('analytics.wind.speedTimeSeriesTitle')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.wind.speedTimeSeriesTooltip')} />
        </div>
        
        <div className="scientific-chart">
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={windSpeedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis
                dataKey="time"
                tickFormatter={(value) => format(parseISO(value), xAxisFormat)}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
              />
              <YAxis
                domain={[0, 'auto']}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
                label={{
                  value: 'm/s',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#6B6B6B' }
                }}
              />
              <Tooltip content={<WindSpeedTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              
              <Area type="monotone" dataKey="campo_gust" name={t('analytics.wind.series.fieldGust')} stroke="transparent" fill="#4A7A8A" fillOpacity={0.15} />
              <Area type="monotone" dataKey="retro_gust" name={t('analytics.wind.series.underGust')} stroke="transparent" fill="#5A7A3A" fillOpacity={0.15} />
              <Line type="monotone" dataKey="campo_speed" name={t('analytics.wind.series.fieldSpeed')} stroke="#4A7A8A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="retro_speed" name={t('analytics.wind.series.underSpeed')} stroke="#5A7A3A" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
          
          <div className="scientific-chart__interpretation">
            <strong>{t('analytics.common.interpretationLabel')}:</strong>{' '}
            {t('analytics.wind.speedInterpretation')}
          </div>
        </div>
      </section>

      {/* Wind Rose / Direction Distribution - Analytical */}
      <section className="wind-tab__section">
        <div className="wind-tab__section-header">
          <div className="wind-tab__section-title-group">
            <Compass size={20} className="wind-tab__section-icon" />
            <h3 className="wind-tab__section-title">{t('analytics.wind.roseTitle')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.wind.roseTooltip')} />
        </div>
        
        {windRoseData && windRoseData.directions && (
          <div className="wind-tab__rose-container-analytical">
            <div className="wind-rose-analytical">
              <div className="wind-rose-analytical__compass">
                {/* Direction Labels */}
                <span className="wind-rose-analytical__label wind-rose-analytical__label--n">{t('analytics.wind.cardinal.n')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--ne">{t('analytics.wind.cardinal.ne')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--e">{t('analytics.wind.cardinal.e')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--se">{t('analytics.wind.cardinal.se')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--s">{t('analytics.wind.cardinal.s')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--sw">{t('analytics.wind.cardinal.sw')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--w">{t('analytics.wind.cardinal.w')}</span>
                <span className="wind-rose-analytical__label wind-rose-analytical__label--nw">{t('analytics.wind.cardinal.nw')}</span>
                
                {/* Concentric Circles for Frequency */}
                <div className="wind-rose-analytical__circle" style={{ width: '20%', height: '20%' }} />
                <div className="wind-rose-analytical__circle" style={{ width: '40%', height: '40%' }} />
                <div className="wind-rose-analytical__circle" style={{ width: '60%', height: '60%' }} />
                <div className="wind-rose-analytical__circle" style={{ width: '80%', height: '80%' }} />
                <div className="wind-rose-analytical__circle" style={{ width: '100%', height: '100%' }} />
                
                {/* Frequency Labels on Circles */}
                <span className="wind-rose-analytical__freq-label" style={{ top: '10%', left: '50%' }}>2%</span>
                <span className="wind-rose-analytical__freq-label" style={{ top: '20%', left: '50%' }}>4%</span>
                <span className="wind-rose-analytical__freq-label" style={{ top: '30%', left: '50%' }}>6%</span>
                <span className="wind-rose-analytical__freq-label" style={{ top: '40%', left: '50%' }}>8%</span>
                <span className="wind-rose-analytical__freq-label" style={{ top: '50%', left: '50%' }}>10%</span>
                
                {/* Calm Conditions in Center */}
                <div className="wind-rose-analytical__calm">
                  <div className="wind-rose-analytical__calm-value">
                    {windRoseData.calm?.frequency?.toFixed(1) || '0.0'}%
                  </div>
                  <div className="wind-rose-analytical__calm-label">{t('analytics.wind.calm')}</div>
                </div>
                
                {/* Directional Segments with Speed Bins */}
                {windRoseData.directions.map((dirData, dirIdx) => {
                  const angle = dirIdx * 45;
                  // Find max frequency across all directions to scale properly (max 12% = full radius)
                  const maxFreq = Math.max(...windRoseData.directions.map(d => d.totalFrequency), 12);
                  const scaleFactor = 50 / Math.max(maxFreq, 12); // Scale to 50% of compass (radius)
                  
                  let cumulativeHeight = 0;
                  
                  return (
                    <div
                      key={dirData.direction}
                      className="wind-rose-analytical__direction"
                      style={{ transform: `rotate(${angle}deg)` }}
                    >
                      {dirData.speedSegments.map((segment, segIdx) => {
                        // Calculate height as percentage of radius (50% max)
                        const segmentHeightPercent = (segment.frequency * scaleFactor);
                        const segmentHeight = segmentHeightPercent > 0.3 ? segmentHeightPercent : 0;
                        const currentBottom = cumulativeHeight;
                        
                        if (segmentHeight <= 0) return null;
                        
                        cumulativeHeight += segmentHeight;
                        
                        return (
                          <div
                            key={segIdx}
                            className="wind-rose-analytical__segment"
                            style={{
                              backgroundColor: segment.color,
                              height: `${segmentHeight}%`,
                              bottom: `${currentBottom}%`,
                            }}
                            title={`${dirData.direction}: ${segment.label} m/s (${segment.frequency.toFixed(1)}%)`}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Speed Range Legend */}
            <div className="wind-rose-analytical__legend">
              <h4 className="wind-rose-analytical__legend-title">{t('analytics.wind.legendTitle')}</h4>
              <div className="wind-rose-analytical__legend-items">
                {windRoseData.speedBins.map((bin, idx) => (
                  <div key={idx} className="wind-rose-analytical__legend-item">
                    <div 
                      className="wind-rose-analytical__legend-color" 
                      style={{ backgroundColor: bin.color }}
                    />
                    <span className="wind-rose-analytical__legend-label">{bin.label}</span>
                    <span className="wind-rose-analytical__legend-pct">
                      {bin.frequency.toFixed(1)}%
                    </span>
                  </div>
                ))}
                <div className="wind-rose-analytical__legend-item">
                  <div className="wind-rose-analytical__legend-color wind-rose-analytical__legend-color--calm" />
                  <span className="wind-rose-analytical__legend-label">{t('analytics.wind.calmWithThreshold')}</span>
                  <span className="wind-rose-analytical__legend-pct">
                    {windRoseData.calm?.frequency?.toFixed(1) || '0.0'}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="scientific-chart__interpretation">
          <strong>{t('analytics.common.analyticalInterpretationLabel')}:</strong>{' '}
          {t('analytics.wind.roseInterpretation', { count: windRoseData?.total || 0 })}
        </div>
      </section>

      {/* Wind Direction Time Series */}
      <section className="wind-tab__section">
        <div className="wind-tab__section-header">
          <div className="wind-tab__section-title-group">
            <Compass size={20} className="wind-tab__section-icon" />
            <h3 className="wind-tab__section-title">{t('analytics.wind.directionTimeSeriesTitle')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.wind.directionTimeSeriesTooltip')} />
        </div>
        
        <div className="scientific-chart">
          <ResponsiveContainer width="100%" height={250}>
            <ScatterChart data={windDirectionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis
                dataKey="time"
                tickFormatter={(t) => format(parseISO(t), xAxisFormat)}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
              />
              <YAxis
                domain={[0, 360]}
                ticks={[0, 90, 180, 270, 360]}
                tickFormatter={(v) => {
                  const labels = { 0: t('analytics.wind.cardinal.n'), 90: t('analytics.wind.cardinal.e'), 180: t('analytics.wind.cardinal.s'), 270: t('analytics.wind.cardinal.w'), 360: t('analytics.wind.cardinal.n') };
                  return labels[v] || v;
                }}
                tick={{ fontSize: 11 }}
                stroke="#6B6B6B"
                label={{
                  value: t('analytics.wind.direction'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#6B6B6B' }
                }}
              />
              <Tooltip content={<WindDirTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              
              <Scatter dataKey="direction" fill="#4A7A8A" />
            </ScatterChart>
          </ResponsiveContainer>
          
          <div className="scientific-chart__interpretation">
            <strong>{t('analytics.common.readingLabel')}:</strong>{' '}
            {t('analytics.wind.directionReading')}
          </div>
        </div>
      </section>

      {/* Statistics Summary */}
      <section className="wind-tab__section">
        <div className="wind-tab__section-header">
          <div className="wind-tab__section-title-group">
            <Wind size={20} className="wind-tab__section-icon" />
            <h3 className="wind-tab__section-title">{t('analytics.statsTable.title')}</h3>
          </div>
          <InfoTooltipButton content={t('analytics.wind.statsTooltip')} />
        </div>
        
        {windStats && (
          <div className="statistics-table-container">
            <table className="statistics-table">
              <thead>
                <tr>
                  <th>{t('analytics.statsTable.metric')}</th>
                  <th>{t('stations.types.field')}</th>
                  <th>{t('stations.types.underpanel')}</th>
                  <th>{t('analytics.statsTable.deltaDifference')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('analytics.wind.stats.meanSpeed')}</td>
                  <td>{windStats.speed.campo.mean.toFixed(2)}</td>
                  <td>{windStats.speed.retro.mean.toFixed(2)}</td>
                  <td className="negative">
                    {(windStats.speed.retro.mean - windStats.speed.campo.mean).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.wind.stats.maxSpeed')}</td>
                  <td>{windStats.speed.campo.max.toFixed(1)}</td>
                  <td>{windStats.speed.retro.max.toFixed(1)}</td>
                  <td className="negative">{(windStats.speed.retro.max - windStats.speed.campo.max).toFixed(1)}</td>
                </tr>
                <tr>
                  <td>{t('analytics.wind.stats.maxGust')}</td>
                  <td>{windStats.gust.campo.max.toFixed(1)}</td>
                  <td>{windStats.gust.retro.max.toFixed(1)}</td>
                  <td className="negative">{(windStats.gust.retro.max - windStats.gust.campo.max).toFixed(1)}</td>
                </tr>
                <tr>
                  <td>{t('analytics.wind.stats.calmHours')}</td>
                  <td>{windStats.calmHours.campo}%</td>
                  <td>{windStats.calmHours.retro}%</td>
                  <td className="positive">
                    +{(parseFloat(windStats.calmHours.retro) - parseFloat(windStats.calmHours.campo)).toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>{t('analytics.wind.stats.prevailingDirection')}</td>
                  <td colSpan="3">{prevailingDirection}</td>
                </tr>
              </tbody>
            </table>
            
            <div className="wind-tab__summary-box">
              <TrendingDown size={18} />
              <strong>{t('analytics.wind.windReductionLabel')}:</strong>{' '}
              {t('analytics.wind.windReductionValue', { percent: windReduction })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

